import {
  applyRecordTemplate,
  type MappingTemplate,
} from "@/lib/portalData";

/** One record-type to stamp on the new connector: which template, the source
 *  view it reads, and the per-slot bindings. */
export type PlannedRecord = {
  /** client-only row id */
  id: number;
  templateKey: string;
  version: number;
  recordName: string;
  recordSource: string;
  bindings: Record<string, string>;
};

/** Stamp the planned record-templates onto a freshly-created connector (with
 *  create_if_absent). Each apply is INDEPENDENT and non-fatal — a per-record
 *  failure is collected and returned (`"Record: reason"`), never aborting the
 *  rest, so the connector create is never undone by a bad template. Returns the
 *  list of failure messages (empty = all applied). */
export async function applyPlannedRecords(
  token: string,
  connectorId: number,
  records: PlannedRecord[],
): Promise<string[]> {
  const failed: string[] = [];
  for (const rec of records) {
    try {
      await applyRecordTemplate(token, connectorId, rec.recordName, {
        templateKey: rec.templateKey,
        version: rec.version,
        bindings: rec.bindings,
        createIfAbsent: true,
        recordSource: rec.recordSource.trim(),
      });
    } catch (e) {
      failed.push(`${rec.recordName}: ${e instanceof Error ? e.message : "failed"}`);
    }
  }
  return failed;
}

/** Client-side validation of the planned records before create: each needs a
 *  source view + every REQUIRED binding of its template. Returns the first error
 *  message, or null if all are complete. Pure, so the form + tests share it.
 *  (The backend enforces the same via 400 — this is a friendlier pre-check.) */
export function validatePlannedRecords(
  records: PlannedRecord[],
  templates: MappingTemplate[],
): string | null {
  for (const r of records) {
    if (!r.recordSource.trim()) {
      return `Enter a source view for the ${r.recordName} record`;
    }
    const t = templates.find(
      (x) => x.key === r.templateKey && x.version === r.version,
    );
    const missing = (t?.bindings ?? [])
      .filter((b) => b.required && !(r.bindings[b.slot] ?? "").trim())
      .map((b) => b.label);
    if (missing.length > 0) {
      return `${r.recordName}: fill in ${missing.join(", ")}`;
    }
  }
  return null;
}
